import React, { useState, useMemo } from 'react';
import { Form, Row, Col, Button, Modal } from 'react-bootstrap';
import { IoDocuments, IoEye } from "react-icons/io5";
import axios from 'axios';
import './leave-report.css';
import LeaveLayout from "../../../components/LeaveLayout.jsx"; 
import LeaveReportLayout from "../../../components/LeaveReportLayout.jsx";

export default function LeaveReport() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [showTable, setShowTable] = useState(false);
  const [searchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [tableData, setTableData] = useState([]);
  const [showModal, setShowModal] = useState(false);  // Modal visibility state
  const [selectedLeave, setSelectedLeave] = useState(null);  // Selected leave details
  const rowsPerPage = 5;

  const handleGenerateReport = async () => {
    if (startDate && endDate) {
      setFormErrors({});
      try {
        const response = await axios.get('http://localhost:5544/api/leaves/leaves/reports', {
          params: { startDate, endDate },
        });
        setTableData(response.data.leaves); // Extract `leaves` from the response
        setShowTable(true);
      } catch (error) {
        console.error("Error fetching leave data:", error);
        alert("Failed to fetch leave data. Please try again.");
        setShowTable(false);
      }
    } else {
      setFormErrors({
        startDate: !startDate ? 'Start date is required' : '',
        endDate: !endDate ? 'End date is required' : '',
      });
      setShowTable(false);
    }
  };

  const filteredData = useMemo(() => {
    return tableData.filter(row =>
      row.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.leaveType.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery, tableData]);

  const currentRows = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [currentPage, filteredData]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const pageRange = () => {
    const range = [];
    for (let i = 1; i <= totalPages; i++) {
      range.push(i);
    }
    return range;
  };

  const handleEyeClick = (leave) => {
    setSelectedLeave(leave);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedLeave(null);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString(); // Format the date in a readable way
  };

  return (
    <LeaveLayout>
      <LeaveReportLayout>
        <div className="leave-report-container">
          <h1 className="leave-report-header"><IoDocuments /> Leave Reports</h1>
          <hr />
          <Form className="leave-report-form">
            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label htmlFor="start_date">
                    Start Date <span className="required">*</span>:
                  </Form.Label>
                  <Form.Control
                    type="date"
                    id="start_date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    isInvalid={!!formErrors.startDate}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.startDate}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label htmlFor="end_date">
                    End Date <span className="required">*</span>:
                  </Form.Label>
                  <Form.Control
                    type="date"
                    id="end_date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    isInvalid={!!formErrors.endDate}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.endDate}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Button className="generate-report-button" onClick={handleGenerateReport}>
              Generate Report
            </Button>
          </Form>

          {showTable && (
            <div className="leave-report-table-container">
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead className='leave-report-table-header'>
                    <tr className='table-header'>
                      <th>SR</th>
                      <th>Name</th>
                      <th>Leave Type</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Applied Duration</th>
                      <th>Applied Date</th>
                      <th>HR Approval</th>
                      <th>Admin Approval</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentRows.length > 0 ? (
                      currentRows.map((row, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{row.name}</td>
                          <td>{row.leaveType}</td>
                          <td>{formatDate(row.startDate)}</td>
                          <td>{formatDate(row.endDate)}</td>
                          <td>{row.days} days</td>
                          <td>{formatDate(row.appliedDate)}</td>
                          <td>{row.hrAction}</td>
                          <td>{row.adminAction}</td>
                          <td>
                            <Button 
                              variant='primary' 
                              size='sm' 
                              onClick={() => handleEyeClick(row)}
                            >
                              <IoEye />
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="9" className="text-center">
                          No data available.
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot className='leave-report-table-footer'>
                    <tr>
                      <td>Total</td>
                      <td>{currentRows.length}</td> {/* Display the count of current rows */}
                      <td colSpan="8"></td> {/* Adjust other columns accordingly */}
                    </tr>
                  </tfoot>
                </table>
                <div className="pagination-container">
                  <nav>
                    <ul className="pagination">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>
                          Previous
                        </button>
                      </li>
                      {pageRange().map((page) => (
                        <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => handlePageChange(page)}>
                            {page}
                          </button>
                        </li>
                      ))}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </div>
          )}

          {/* Stylish Modal */}
          <Modal
            show={showModal}
            onHide={handleCloseModal}
            centered // Center the modal on the screen
            className="custom-modal"
          >
            <Modal.Header closeButton className="custom-modal-header">
              <Modal.Title>Leave Details</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {selectedLeave && (
                <>
                  <p>
                    <strong>Name:</strong> {selectedLeave.name}
                  </p>
                  <p>
                    <strong>Leave Type:</strong> {selectedLeave.leaveType}
                  </p>
                  <p>
                    <strong>Start Date:</strong>{" "}
                    {formatDate(selectedLeave.startDate)}
                  </p>
                  <p>
                    <strong>End Date:</strong> {formatDate(selectedLeave.endDate)}
                  </p>
                  <p>
                    <strong>Applied Duration:</strong> {selectedLeave.days} day(s)
                  </p>
                  <p>
                    <strong>Applied Date:</strong>{" "}
                    {formatDate(selectedLeave.appliedDate)}
                  </p>
                  <p>
                    <strong>HR Approval:</strong> {selectedLeave.hrAction}
                  </p>
                  <p>
                    <strong>Admin Approval:</strong> {selectedLeave.adminAction}
                  </p>
                </>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Close
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      </LeaveReportLayout>
    </LeaveLayout>
  );
}
